import { CandidateRegistrationDtoClient } from "@/api/candidate/dto/candidate-registration.dto";
import { AddressDto } from "@/api/dto/address.dto";
import { Role } from "@/api/enums/Role";
import {
  getAddressDefaultValues,
  mandatoryAddressSchema,
  optionalAddressSchema,
} from "@/components/molecules/AddressForm/AddressFormSchema";
import { date, nullDate } from "@/constants/zodTypes";
import { PayementUtil } from "@/lib/payement-util";
import { RegistrationUtil } from "@/lib/registration-util";
import { z } from "zod";

const payementAcknowledgeSchema = (
  registration: CandidateRegistrationDtoClient,
) =>
  z
    .object({
      date: date(),
      payer: z.nativeEnum(Role),
      billingAddress: optionalAddressSchema,
    })
    .superRefine(({ payer, billingAddress }, ctx) => {
      if (!RegistrationUtil.missingBillingAddress(registration, payer)) return;

      const result = mandatoryAddressSchema.safeParse(billingAddress);
      if (result.success) return;

      result.error.issues.forEach((issue) =>
        ctx.addIssue({ ...issue, path: ["billingAddress", ...issue.path] }),
      );
    });

type PayementAcknowledgeSchema = z.infer<
  ReturnType<typeof payementAcknowledgeSchema>
>;

// the candidate's own address is the admin's best starting point: a delegation
// registration never carried a billing address of its own
const toFormAddress = (address?: AddressDto) => {
  const defaults = getAddressDefaultValues();

  if (!address) return defaults;

  return {
    country: address.country ?? defaults.country,
    personalAddress: address.personalAddress ?? defaults.personalAddress,
    complement: address.complement ?? defaults.complement,
    postalCode: address.postalCode ?? defaults.postalCode,
    city: address.city ?? defaults.city,
  };
};

const payementAcknowledgeDefaultValues = (
  registration: CandidateRegistrationDtoClient,
  candidateAddress?: AddressDto,
): PayementAcknowledgeSchema => {
  const { payementStatus } = registration;

  return {
    date: nullDate(),
    payer: PayementUtil.isWaitingValidation(payementStatus)
      ? PayementUtil.payerOf(payementStatus)
      : RegistrationUtil.hasNonRefusedDelegation(registration)
        ? Role.COMPANY
        : Role.CANDIDATE,
    billingAddress: toFormAddress(candidateAddress),
  };
};

export { payementAcknowledgeDefaultValues, payementAcknowledgeSchema };
export type { PayementAcknowledgeSchema };
